import { fetchAuthSession } from 'aws-amplify/auth';

interface AppConfig {
  apiUrl: string;
}

const LOCAL_DEV_USER_KEY = 'local-dev-user-role';

function isLocalDev(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_USER_POOL_ID;
}

function getApiUrl(): string {
  if (isLocalDev()) {
    return 'http://localhost:3001';
  }
  const config = (window as unknown as { appConfig: AppConfig }).appConfig;
  return config?.apiUrl || 'http://localhost:3001';
}

function getDevUserRole(): string | null {
  if (!isLocalDev()) return null;
  return localStorage.getItem(LOCAL_DEV_USER_KEY) || 'superAdmin';
}

async function getAuthToken(): Promise<string> {
  // Skip auth in local dev mode
  if (isLocalDev()) {
    return '';
  }

  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch {
    return '';
  }
}

async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const apiUrl = getApiUrl();
  const token = await getAuthToken();
  const devUserRole = getDevUserRole();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // In local dev mode, send the selected user role to the backend
  if (devUserRole) {
    headers['X-Dev-User-Role'] = devUserRole;
  }

  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

export function useApi() {
  return {
    get: <T>(path: string) => apiRequest<T>(path),
    post: <T>(path: string, data: unknown) =>
      apiRequest<T>(path, { method: 'POST', body: JSON.stringify(data) }),
    put: <T>(path: string, data: unknown) =>
      apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(data) }),
    delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  };
}
